import { useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Warning, ArrowSquareOut, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { fmtDateTime } from "../../lib/dateUtil";
import { ScopeText } from "../jobs/ScopeOfWorksField";

const sourcePath = (f) => (f.source_type === "job_request" ? `/job-requests/${f.source_id}` : `/jobs/${f.source_id}`);

/* Flag shape: {source_type, source_id, source_ref, changed_at, changed_by, changes: [{field, label, source, quote}]} */
export const driftChanges = (flag) => flag?.changes || (flag?.source_scope !== undefined ? [{ field: "scope_of_works", label: "Scope of Works", source: flag.source_scope, quote: flag.quote_scope }] : []);

const SHORT = { scope_of_works: "Scope", caveats: "Caveats", notice: "Safety notice" };
export const driftTitle = (flag) => {
  const labels = driftChanges(flag).map((c) => SHORT[c.field] || c.label);
  return labels.length > 1 ? `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}` : labels[0] || "Scope";
};

function TermValue({ field, value, emptyText }) {
  if (field === "notice") {
    if (!value?.text) return <p className="text-xs italic text-muted-foreground">{emptyText}</p>;
    return (
      <div className="text-xs">
        {value.title && <p className="font-semibold">{value.title}</p>}
        <p className="whitespace-pre-wrap">{value.text}</p>
      </div>
    );
  }
  return value ? <ScopeText text={value} className="text-xs" /> : <p className="text-xs italic text-muted-foreground">{emptyText}</p>;
}

/* Small pill for lists / quote cards. */
export function ScopeDriftPill({ flag, testId }) {
  if (!flag || !driftChanges(flag).length) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200"
          title={`${driftTitle(flag)} on ${flag.source_ref} changed after this quote was built`} data-testid={testId}>
      <Warning size={10} weight="fill" /> {driftTitle(flag)} changed
    </span>
  );
}

/* Amber banner on the quote page: the request's / job's scope, caveats or safety notice moved on after this quote went out. */
export default function ScopeDriftBanner({ quote, onResolved }) {
  const [busy, setBusy] = useState("");
  const f = quote?.scope_drift;
  const changes = driftChanges(f);
  if (!f || !changes.length) return null;
  const resolve = async (action) => {
    setBusy(action);
    try {
      const { data } = await API.post(`/quotes/${quote.id}/scope-drift/resolve`, { action });
      const extra = [data.wos?.length ? `works order ${data.wos.join(", ")} updated` : "", data.job ? `job ${data.job} updated` : ""].filter(Boolean).join(" · ");
      toast.success(`${data.message}${extra ? ` — ${extra}` : ""}`);
      onResolved?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update the quote"); }
    finally { setBusy(""); }
  };
  return (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 p-4" data-testid="quote-scope-drift-banner">
      <div className="flex items-start gap-3">
        <Warning size={20} weight="fill" className="text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100" data-testid="scope-drift-title">
            {driftTitle(f)} changed on {f.source_ref} after this quote was {quote.status}
          </p>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
            Edited by {f.changed_by || "a colleague"} · {fmtDateTime(f.changed_at)} — decide which wording stands so the quote, job and works order don't drift apart.
            <Link to={sourcePath(f)} className="inline-flex items-center gap-1 ml-2 underline underline-offset-2" data-testid="scope-drift-open-source">Open {f.source_ref} <ArrowSquareOut size={11} /></Link>
          </p>
          <div className="space-y-3 mt-3">
            {changes.map((c) => (
              <div key={c.field} data-testid={`scope-drift-change-${c.field}`}>
                {changes.length > 1 && <p className="text-[11px] font-semibold text-amber-900 dark:text-amber-100 mb-1">{c.label}</p>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-white/70 dark:bg-black/20 p-3" data-testid={`scope-drift-source-${c.field}`}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300 mb-1">{f.source_ref} now says</p>
                    <TermValue field={c.field} value={c.source} emptyText={`${c.label} removed`} />
                  </div>
                  <div className="rounded-md border border-border bg-white/70 dark:bg-black/20 p-3" data-testid={`scope-drift-quote-${c.field}`}>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">This quote says</p>
                    <TermValue field={c.field} value={c.quote} emptyText={`No ${c.label.toLowerCase()} on the quote`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => resolve("accept")} disabled={!!busy} data-testid="scope-drift-accept-btn">
              <ArrowsClockwise size={14} className="mr-1" /> {busy === "accept" ? "Updating…" : `Update quote to match ${f.source_ref}`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => resolve("keep")} disabled={!!busy} data-testid="scope-drift-keep-btn">
              <Check size={14} className="mr-1" /> Keep quote wording
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
